import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import Card from './Card.jsx';
import Button from './Button.jsx';
import { pushState, enablePush, disablePush } from '../../utils/push.js';

const DISMISSED_KEY = 'push-banner-dismissed';

const HINTS = {
  'ios-install': 'Op iPhone en iPad werken pushmeldingen alleen als je de app aan je beginscherm hebt toegevoegd (Deel → Zet op beginscherm). Open hem daarna vanaf het beginscherm.',
  unsupported: 'Deze browser ondersteunt geen pushmeldingen.',
  disabled: 'Pushmeldingen zijn nog niet beschikbaar.',
  denied: 'Je hebt meldingen voor deze site geblokkeerd. Zet ze aan via de instellingen van je browser en probeer het daarna opnieuw.',
};

function usePush() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { pushState().then(setState).catch(() => setState('unsupported')); }, []);

  async function run(action) {
    setBusy(true);
    setError(null);
    try { setState(await action()); }
    catch { setError('Dat is niet gelukt. Probeer het later opnieuw.'); }
    setBusy(false);
  }
  return { state, busy, error, enable: () => run(enablePush), disable: () => run(disablePush) };
}

// Profile: the switch for this device.
export function PushSettingsCard() {
  const { state, busy, error, enable, disable } = usePush();
  if (!state) return null;

  return (
    <Card title="🔔 Pushmeldingen">
      <div style="display:flex;flex-direction:column;gap:10px;font-size:13px">
        <div style="color:var(--color-text-muted)">
          Krijg je meldingen ook als pushmelding op dit apparaat, ook als de app dicht is.
          Dit geldt per apparaat en browser.
        </div>
        {HINTS[state] ? (
          <div>{HINTS[state]}</div>
        ) : (
          <div style="display:flex;align-items:center;justify-content:space-between;gap:12px">
            <span>{state === 'on' ? 'Aan op dit apparaat' : 'Uit op dit apparaat'}</span>
            {state === 'on'
              ? <Button variant="secondary" size="sm" disabled={busy} onClick={disable}>Zet uit</Button>
              : <Button size="sm" disabled={busy} onClick={enable}>Zet aan</Button>}
          </div>
        )}
        {error && <div style="color:var(--color-danger)">{error}</div>}
      </div>
    </Card>
  );
}

// Notifications screen: a one-time offer while push is off on this device.
export function PushBanner() {
  const { state, busy, error, enable } = usePush();
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(DISMISSED_KEY); } catch { return false; }
  });

  if (dismissed || state !== 'off') return null;

  function dismiss() {
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* private mode: just hide for now */ }
    setDismissed(true);
  }

  return (
    <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:12px 14px;margin-bottom:16px;border:1px solid var(--color-border);border-radius:10px;background:var(--color-bg-secondary);font-size:13px">
      <span style="flex:1;min-width:200px">🔔 Wil je meldingen ook als pushmelding op dit apparaat krijgen?</span>
      <div style="display:flex;gap:8px">
        <Button size="sm" disabled={busy} onClick={enable}>Zet aan</Button>
        <Button variant="secondary" size="sm" onClick={dismiss}>Niet nu</Button>
      </div>
      {error && <div style="width:100%;color:var(--color-danger)">{error}</div>}
    </div>
  );
}
